import { AbsoluteTimeRange, TimeRange, TimeRangeKind } from '@nirvana/shared/dtos/types/shared/time';
import { getFormattedFromAbsoluteTimeRange } from '@nirvana/shared/time';
import { BaseFilterConfig } from '../grid/utils/filters/types';
import { utcToZonedTime } from 'date-fns-tz';
import { startOfDay, startOfHour, subDays } from 'date-fns';

export function getUTCTimestampsFromDateFilter(
  timeRange: TimeRange | null | undefined,
  timezone: string
): AbsoluteTimeRange | null {
  if (!timeRange) {
    return null;
  }

  if (timeRange.kind === TimeRangeKind.RELATIVE_TIME_RANGE && timeRange.relative) {
    const { lastNumDays } = timeRange.relative;
    return getAbsoluteTimerangeFromRelativeInterval(lastNumDays, timezone);
  }

  if (timeRange.kind === TimeRangeKind.ABSOLUTE_TIME_RANGE && timeRange.absolute) {
    return getFormattedFromAbsoluteTimeRange(timeRange.absolute);
  }

  return null;
}

export function getAbsoluteTimerangeFromRelativeInterval(lastNumDays: number, timezone: string): AbsoluteTimeRange {
  /**
   * The specs for this function can be found here:
   * https://bitdefender-bsg.atlassian.net/browse/CERES-11877
   */
  const now = new Date();
  const timestamp = lastNumDays === 1 ? startOfHour(now).getTime() : startOfDay(now).getTime();

  let fromTimestamp = subDays(new Date(timestamp), lastNumDays).getTime();

  // when Last 7 days or Last 30 days is selected take into account the timezone
  // such that the first hour of the day (00:00) is the first hour of the day in that timezone
  if (timezone && lastNumDays > 1) {
    const timezoneOffset = fromTimestamp - utcToZonedTime(new Date(fromTimestamp), timezone).valueOf();
    fromTimestamp += timezoneOffset;
  }

  return {
    fromTimestamp,
    toTimestamp: now.getTime(),
  };
}

export function getFilterConfigsByKey(filterConfigs: Array<BaseFilterConfig>, key: string): BaseFilterConfig {
  const filterConfig = filterConfigs.find((config: BaseFilterConfig) => {
    return config.key === key;
  });

  if (!filterConfig) {
    // This is a programmatic error, it means you sent less filter configs than filter values.
    throw new Error(`Unexpected filter with key ${key} - missing filter value`);
  }

  return filterConfig;
}
